import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { IMAGE_FORMATS, VIDEO_FORMATS } from "../../utils/constants";
import PDFImage from "../../assets/img/pdf-icon.png";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import linkifyHtml from "linkify-html";
import {
  BUTTON_CHANGES_APPLIED,
  BUTTON_PREVIEW_CHANGES,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import CardMedia from "../Media/CardMedia";
import { getExtensionFile } from "../../utils/string";
import Loader from "../Loaders/Loader";
import MediaDownloadContainer from "../Containers/MediaDownloadContainer";
import { useDispatchApp } from "../../lib/redux";
import { startGetAwsDownloadUrl } from "../../actions/user";
import InsightCard from "../../pages/AdsCampaignStatistics/components/PerformingAssets/components/InsightCard";
import InsightActionableContainer from "../Containers/InsightActionableContainer";
import { useMediaQuery, useTheme } from "@mui/material";
import ModalPreviewOptimization from "../../pages/NewAdsCampaign/components/Common/ModalPreviewOptimization";
import ChatReasoningIndicator from "./ChatReasoningIndicator";
import CampaignLearnings from "./CampaignLearnings";
import OpeningAssistHighlight from "./OpeningAssistHighlight";

const ChatCustomMessage = ({
  message = {},
  messageKey = null,
  direction,
  embedded,
  onChangeStatusOptimization = () => {},
  showPreviewOptimization = true,
  optimizationsSingleColumn = false,
  disabledInteractions = false,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentOptimization, setCurrentOptimization] = useState(null);
  const [optimizationIndex, setOptimizationIndex] = useState(0);

  const hasMedia = message?.mediaURL;
  const ext = hasMedia ? getExtensionFile(message?.mediaURL) : "";

  const allOptimizations = message?.optimizations || [];
  const optimizations = allOptimizations.filter(
    (o) => !o.isApplied && !o.isDiscarded
  );

  const learnings = message?.learnings || [];
  const insights = message?.insights || [];

  const { t } = useTranslationApp();
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const isMobile = useMediaQuery("(max-width: 1100px)");

  // Handle opening the optimization modal
  const onOpenOptimizationModal = (optimization, index) => {
    setCurrentOptimization(optimization);
    setOptimizationIndex(index);
    setModalOpen(true);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentOptimization(null);
  };

  const getStylesMessage = () => {
    if (
      optimizations?.length > 0 ||
      message?.learnings?.length > 0 ||
      insights?.length > 0
    ) {
      return {
        backgroundColor: "transparent",
        color: "#000",
      };
    }

    if (hasMedia) {
      return {
        backgroundColor: "inherit",
        color: "background.paperSecondaryContrastText",
      };
    }

    if (direction === "incoming") {
      return {
        background: theme.palette.button.primary.main,
        color: theme.palette.button.primary.contrastText,
      };
    }

    return {
      backgroundColor: "#f5f5f5",
      color: "#000",
    };
  };

  const stylesMessage = getStylesMessage();
  const isReasoning = !!message?.isReasoning;
  const hasVisibleOptimizationCards = optimizations.length > 0;
  const showOpeningAssist =
    embedded && direction === "incoming" && !isReasoning && messageKey != null;

  const bubbleContent = (
    <Box
      sx={{
        background: isReasoning ? "transparent" : stylesMessage.background,
        backgroundColor: isReasoning
          ? "transparent"
          : stylesMessage.backgroundColor,
        borderRadius: 3,
        padding: isReasoning ? 0 : 1.5,
        px:
          optimizations?.length > 0 ||
          message?.learnings?.length > 0 ||
          insights?.length > 0
            ? 0
            : 1.5,
        mt: isReasoning ? 0.5 : 0,
      }}
    >
      {isReasoning ? (
        <ChatReasoningIndicator messageKeys={message?.reasoningMessageKeys} />
      ) : learnings.length > 0 ? (
        <CampaignLearnings learnings={learnings} />
      ) : (
        <>
          {message?.message ? (
            <Typography
              variant="body2"
              sx={{
                color: stylesMessage.color,
                fontSize: ".91rem",
                whiteSpace: "preWrap",
                overflowWrap: "anywhere",
                wordBreak: "breakWord",
                fontFeatureSettings: "normal",
                fontVariant: "normal",
                mb: hasMedia || insights.length > 0 ? 1 : 0,
                px: insights.length > 0 ? 1.5 : 0,
              }}
              dangerouslySetInnerHTML={{
                __html: linkifyHtml(message?.message || "", {
                  attributes: {
                    rel: "noopener noreferrer",
                    target: "_blank",
                    style: "color: #00A3FF",
                  },
                }),
              }}
            />
          ) : null}
          {insights.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: message?.message ? 1 : 0,
              }}
            >
              {insights.map((insight) => (
                <Box
                  key={insight.id || insight.title}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {insight.title}
                  </Typography>
                  {insight.description ? (
                    <Typography variant="body2" color="text.secondary">
                      {insight.description}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
      {hasMedia &&
      !IMAGE_FORMATS.includes(ext) &&
      !VIDEO_FORMATS.includes(ext) ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              borderRadius: 6,
              py: 1,
              px: 2,
            }}
          >
            <Avatar
              src={PDFImage}
              alt="PDF icon"
              variant="square"
              sx={{
                width: 30,
                height: "auto",
              }}
            />
            {isFetching ? (
              <Loader color="secondary" size={30} hasMarginTop={false} />
            ) : (
              <IconButton
                sx={{
                  border: "1px solid white",
                }}
                onClick={async () => {
                  setIsFetching(true);
                  await dispatch(startGetAwsDownloadUrl(message.mediaURL));
                  setIsFetching(false);
                }}
              >
                <DownloadIcon
                  sx={{
                    color: "white",
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Box>
      ) : null}
      {hasMedia && IMAGE_FORMATS.includes(ext) ? (
        <MediaDownloadContainer url={message.mediaURL} alt={message.message}>
          <Avatar
            variant="square"
            sx={{
              width: "auto",
              maxWidth: 200,
              height: "auto",
              borderRadius: 5,
            }}
            src={message.mediaURL}
            alt={message.message}
          />
        </MediaDownloadContainer>
      ) : null}
      {hasMedia && VIDEO_FORMATS.includes(ext) ? (
        <MediaDownloadContainer
          url={message.mediaURL}
          alt={message.message}
          onClick={() => {
            const video = document.getElementById("video-container");
            if (video) {
              video.pause();
              video.muted = true;
            }
          }}
          onClose={() => {
            const video = document.getElementById("video-container");
            if (video) {
              video.muted = false;
              video.pause();
            }
          }}
        >
          <CardMedia
            id="video-container"
            url={message.mediaURL}
            propsType={{
              controlsList: "nodownload",
              controls: true,
            }}
            sx={{
              width: "100%",
              maxWidth: 200,
              height: "auto",
              borderRadius: 25,
              position: "relative",
            }}
          />
        </MediaDownloadContainer>
      ) : null}
    </Box>
  );

  return (
    <>
      {showOpeningAssist && !hasVisibleOptimizationCards ? (
        <OpeningAssistHighlight
          messageKey={messageKey}
          hasOptimizationCards={false}
        >
          {bubbleContent}
        </OpeningAssistHighlight>
      ) : (
        bubbleContent
      )}

      {optimizations?.length > 0 &&
        (() => {
          const optimizationsList = (
            <Box
              sx={{
                display: "flex",
                flexWrap: optimizationsSingleColumn ? "nowrap" : "wrap",
                gap: 2,
                mt: 3,
                mb: 1,
                pb: 1.5,
                backgroundColor: "transparent",
                borderRadius: 0,
                padding: 0,
                width: "100%",
                flexDirection: optimizationsSingleColumn ? "column" : "row",
              }}
            >
              {optimizations.map((optimization, index) => {
                const isApplied = optimization.isApplied;
                const isDiscarded = optimization.isDiscarded;

                return (
                  <InsightCard
                    key={optimization.id}
                    title={optimization.title}
                    platform={optimization.platform}
                    category={optimization.category}
                    sx={{
                      height: "auto",
                      minHeight: "222px",
                      width: optimizationsSingleColumn
                        ? "100%"
                        : isMobile || !embedded
                          ? "100%"
                          : "48%",
                      boxShadow: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                    index={index}
                    showDiscardButton={!disabledInteractions}
                    isOptimizationApplied={isApplied}
                    isOptimizationDiscarded={isDiscarded}
                    onDiscardButtonClick={() => {
                      if (disabledInteractions) {
                        return;
                      }
                      onChangeStatusOptimization({
                        optimization,
                        status: "discarded",
                      });
                    }}
                    nextContent={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          justifyContent: "space-between",
                          flex: 1,
                          minHeight: 0,
                          overflow: "visible",
                        }}
                      >
                        <InsightActionableContainer
                          insight={optimization}
                          disabled={isApplied || disabledInteractions}
                          showActionButton={true}
                          actionButtonText={
                            isApplied
                              ? t(BUTTON_CHANGES_APPLIED)
                              : t(BUTTON_PREVIEW_CHANGES)
                          }
                          onActionButtonClick={() => {
                            if (
                              disabledInteractions ||
                              optimization.isApplied
                            ) {
                              return;
                            }

                            if (!showPreviewOptimization) {
                              onChangeStatusOptimization({
                                optimization,
                                status: "applied",
                              });
                              return;
                            }
                            onOpenOptimizationModal(optimization, index);
                          }}
                        />
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          );

          if (showOpeningAssist && hasVisibleOptimizationCards) {
            return (
              <OpeningAssistHighlight
                messageKey={messageKey}
                hasOptimizationCards={true}
              >
                {optimizationsList}
              </OpeningAssistHighlight>
            );
          }

          return optimizationsList;
        })()}

      {/* Optimization Preview Modal */}
      {modalOpen && currentOptimization && (
        <ModalPreviewOptimization
          modalOpen={modalOpen}
          insight={currentOptimization}
          insightIndex={optimizationIndex}
          onCloseModal={handleCloseModal}
          onCallbackApplyOptimization={(params) =>
            onChangeStatusOptimization({ ...params, status: "applied" })
          }
          onCallbackDiscardOptimization={(params) =>
            onChangeStatusOptimization({ ...params, status: "discarded" })
          }
        />
      )}
    </>
  );
};

export default ChatCustomMessage;
